import { useCallback, useEffect, useState } from 'react';

import { useCartContinuePricing } from 'features/cart/hooks/use-cart-continue/use-cart-continue-pricing';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { calculateCartSubTotal } from 'features/cart/utils';
import { useStoreContext } from 'features/store';

import { useOrderContext } from 'state/order';
import { ServiceMode } from 'state/service-mode/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import noop from 'utils/noop';

import { useCheckoutContext } from './use-checkout-context';

type PendingCheckoutReprice = {
  needsLocalReprice: boolean;
  waitForStoreReprice: boolean;
};

/**
 * Checkout V2 helper to (re)price the current order using the same PriceOrderV2
 * flow used on cart continue for minimal cart.
 *
 * Store-switch repricing waits for unified-menu readiness, updates the live minimal cart,
 * then invokes shared pricing so checkout can refetch the newly priced order.
 */
export const useCheckoutPriceOrder = () => {
  const enableMinimalCartUi = useMinimalCartUi();
  const { store } = useStoreContext();
  const { setCheckoutRequestedSubtotalCents } = useOrderContext();
  const { serverOrder } = useCheckoutContext();
  const {
    cartEntries,
    repriceMinimalCartEntries,
    shouldRepriceMinimalCartForStore,
  } = useMinimalCart();
  const {
    indexedMenu,
    loadedStoreNumber,
    isLoading: isUnifiedMenuLoading,
  } = useUnifiedMenuContext();
  const { startPricing } = useCartContinuePricing({
    cartEntries,
    onSuccess: noop,
  });
  const [
    pendingCheckoutReprice,
    setPendingCheckoutReprice,
  ] = useState<PendingCheckoutReprice | null>(null);
  const currentStoreNumber = store?.number ?? null;
  const isStoreMenuReady = Boolean(
    currentStoreNumber &&
      loadedStoreNumber === currentStoreNumber &&
      indexedMenu.isLoaded() &&
      !isUnifiedMenuLoading
  );

  const queueCheckoutReprice = useCallback(
    (mode: ServiceMode, waitForStoreReprice = false) => {
      if (!enableMinimalCartUi || !mode || !serverOrder?.rbiOrderId || cartEntries.length === 0) {
        return;
      }

      setPendingCheckoutReprice({
        needsLocalReprice: true,
        waitForStoreReprice,
      });
    },
    [cartEntries.length, enableMinimalCartUi, serverOrder?.rbiOrderId]
  );

  const priceCheckoutOrderForServiceMode = useCallback(
    (mode: ServiceMode) => {
      queueCheckoutReprice(mode);
    },
    [queueCheckoutReprice]
  );

  const priceCheckoutOrderForStoreChange = useCallback(
    (mode: ServiceMode) => {
      queueCheckoutReprice(mode, true);
    },
    [queueCheckoutReprice]
  );

  useEffect(() => {
    if (!pendingCheckoutReprice) {
      return;
    }

    if (pendingCheckoutReprice.waitForStoreReprice && !isStoreMenuReady) {
      return;
    }

    if (pendingCheckoutReprice.needsLocalReprice) {
      repriceMinimalCartEntries();
      setPendingCheckoutReprice(current =>
        current
          ? {
              ...current,
              needsLocalReprice: false,
            }
          : current
      );
      return;
    }

    setCheckoutRequestedSubtotalCents(calculateCartSubTotal(cartEntries));
    startPricing();
    setPendingCheckoutReprice(null);
  }, [
    cartEntries,
    currentStoreNumber,
    indexedMenu,
    isStoreMenuReady,
    isUnifiedMenuLoading,
    loadedStoreNumber,
    pendingCheckoutReprice,
    repriceMinimalCartEntries,
    setCheckoutRequestedSubtotalCents,
    startPricing,
    shouldRepriceMinimalCartForStore,
  ]);

  return { priceCheckoutOrderForServiceMode, priceCheckoutOrderForStoreChange } as const;
};
